import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { TeamInviteContent } from './components';
import { teamInviteActions } from './store/actions';
import { selectInviteConnectionMethods, selectInviteError, selectInviteExist } from './selectors';
import { SuccessPage } from './components/success-page';
import { ErrorPage } from './components/error-page';

export interface TeamInviteFormValues {
  firstName: string;
  lastName: string;
  password: string;
}

type SocialOwnershipProvider = Exclude<TeamInviteModule.InviteOwnershipMethod, 'password'>;

const ALL_INVITE_USER_METHODS: TeamInviteModule.InviteOwnershipMethod[] = ['password', 'google-oauth2', 'windowslive'];
const SOCIAL_SIGNUP_PROGRESS_KEY = 'team-invite-social-auth-in-progress';
const INVITE_CODE_STORAGE_KEY = 'team-invite-code';

export function TeamInvite(): ReactElement {
  const dispatch = useDispatch();
  const location = useLocation();

  const [successLink, setSuccessLink] = useState<string>(null);
  const [inviteCode, setInviteCode] = useState<string>(null);
  const [isInviteCodeResolved, setIsInviteCodeResolved] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedMethod, setSelectedMethod] = useState<TeamInviteModule.InviteOwnershipMethod>('password');
  const [socialToken, setSocialToken] = useState<string>(null);
  const [socialProvider, setSocialProvider] = useState<SocialOwnershipProvider | null>(null);

  const isExist = useSelector(selectInviteExist);
  const inviteError = useSelector(selectInviteError);
  const inviteConnectionMethods = useSelector(selectInviteConnectionMethods);

  const availableMethods = useMemo<TeamInviteModule.InviteOwnershipMethod[]>(() => {
    if (!isExist) return ALL_INVITE_USER_METHODS;
    return inviteConnectionMethods.length ? inviteConnectionMethods : ['password'];
  }, [isExist, inviteConnectionMethods]);

  const initialValues: TeamInviteFormValues = {
    firstName: '',
    lastName: '',
    password: '',
  };

  const validationSchema = useMemo(
    () =>
      Yup.object({
        ...(!isExist ? { firstName: Yup.string().required('First name is a required field') } : {}),
        ...(!isExist ? { lastName: Yup.string().required('Last name is a required field') } : {}),
        ...(selectedMethod === 'password'
          ? {
              password: Yup.string()
                .required('Password is a required field')
                .matches(
                  /^(?=.*[A-Za-z])(?=.*[0-9])(?=.{6,})/,
                  'Password must be at least 6 characters, one letter and one number'
                ),
            }
          : {}),
      }),
    [isExist, selectedMethod]
  );

  const handleSuccessInvite = (value: string) => {
    setIsSubmitting(false);
    sessionStorage.removeItem(INVITE_CODE_STORAGE_KEY);
    setSuccessLink(value);
  };

  const handleErrorInvite = (value: string) => {
    setIsSubmitting(false);
    if (selectedMethod !== 'password') {
      setSocialToken(null);
    }
    toast.error(value ?? 'Invite user error');
  };

  const handleMethodChange = useCallback(
    (method: TeamInviteModule.InviteOwnershipMethod) => {
      setSelectedMethod(method);
      if (method === 'password') {
        setSocialToken(null);
        setSocialProvider(null);
        return;
      }

      if (method !== socialProvider) {
        setSocialToken(null);
      }
      setSocialProvider(method as SocialOwnershipProvider);
    },
    [socialProvider]
  );

  const handleSocialVerified = useCallback(
    ({ token, provider }: { token: string; provider: SocialOwnershipProvider }) => {
      setSelectedMethod(provider);
      setSocialToken(token);
      setSocialProvider(provider);
    },
    []
  );

  const handleSocialError = useCallback(() => {
    setSocialToken(null);
    toast.error('Failed to signin');
  }, []);

  const handleFormSubmit = (values: TeamInviteFormValues) => {
    if (isSubmitting) return;

    if (!inviteCode) {
      toast.error('Invitation code is required');
      return;
    }

    const usingPassword = selectedMethod === 'password';

    if (!usingPassword && !socialToken) {
      toast.error('Please sign in with the selected social provider before submitting.');
      return;
    }

    setIsSubmitting(true);
    const path = location.pathname;
    dispatch(
      teamInviteActions.updateInvitationUser(
        {
          path,
          code: inviteCode,
          ...(usingPassword ? { password: values.password } : { token: socialToken }),
          ...(!isExist ? { first_name: values.firstName } : {}),
          ...(!isExist ? { last_name: values.lastName } : {}),
        },
        handleSuccessInvite,
        handleErrorInvite
      )
    );
  };

  useEffect(() => {
    if (!availableMethods.includes(selectedMethod)) {
      setSelectedMethod(availableMethods[0] ?? 'password');
    }
  }, [availableMethods, selectedMethod]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlCode = searchParams.get('code');
    const hasState = searchParams.has('state');
    const isSocialInProgress = sessionStorage.getItem(SOCIAL_SIGNUP_PROGRESS_KEY) === 'true';
    const storedCode = sessionStorage.getItem(INVITE_CODE_STORAGE_KEY);

    // During Auth0 callback, query "code" belongs to Auth0. Keep using saved invite code.
    if (urlCode && (!hasState || !isSocialInProgress)) {
      sessionStorage.setItem(INVITE_CODE_STORAGE_KEY, urlCode);
      setInviteCode(urlCode);
      setIsInviteCodeResolved(true);
      return;
    }

    if (storedCode) {
      setInviteCode(storedCode);
      setIsInviteCodeResolved(true);
      return;
    }

    setInviteCode(null);
    setIsInviteCodeResolved(true);
  }, [location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const authError = searchParams.get('error');

    if (!authError) return;

    toast.error('Failed to signin');

    searchParams.delete('error');
    searchParams.delete('error_description');
    searchParams.delete('state');

    const search = searchParams.toString();
    window.history.replaceState({}, '', `${location.pathname}${search ? `?${search}` : ''}`);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isInviteCodeResolved) return;

    if (!inviteCode) {
      toast.error('Invitation code is required');
      dispatch(teamInviteActions.verifyInvationCodeError(new Error('Invitation code is required')));
      return;
    }

    const path = location.pathname;
    dispatch(teamInviteActions.verifyInvationCode({ code: inviteCode, path }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteCode, isInviteCodeResolved, location.pathname]);

  const renderContent = () => {
    if (inviteError) return <ErrorPage />;
    if (successLink) return <SuccessPage successLink={successLink} />;
    return (
      <TeamInviteContent
        availableMethods={availableMethods}
        selectedMethod={selectedMethod}
        onMethodChange={handleMethodChange}
        onSocialVerified={handleSocialVerified}
        onSocialError={handleSocialError}
        isSocialVerified={Boolean(socialToken && selectedMethod !== 'password')}
        socialProvider={socialProvider}
        isSubmitting={isSubmitting}
      />
    );
  };

  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange
      validateOnMount
    >
      {renderContent()}
    </Formik>
  );
}
